import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/product/${product.id}`);
  };

  // 渲染星级评分
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <span key={i} className="text-yellow-400">★</span>
      );
    }

    if (hasHalfStar) {
      stars.push(
        <span key="half" className="text-yellow-400">☆</span>
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <span key={`empty-${i}`} className="text-gray-300">☆</span>
      );
    }

    return stars;
  };

  // 处理产品图片URL
  const getImageUrl = (imgUrl: string): string => {
    if (!imgUrl) return '';
    
    // 如果已经是完整URL，直接返回
    if (imgUrl.startsWith('http://') || imgUrl.startsWith('https://')) {
      return imgUrl;
    }
    
    // 如果是相对路径，添加API基础URL
    if (imgUrl.startsWith('/static') || imgUrl.startsWith('/api/files/')) {
      return `${API_BASE}${imgUrl}`;
    }
    
    // 如果是其他格式，尝试添加/static/images/前缀
    if (!imgUrl.startsWith('/')) {
      return `${API_BASE}/static/images/${imgUrl}`;
    }
    
    return `${API_BASE}${imgUrl}`;
  };

  const imgUrl = product.images && product.images[0] ? product.images[0] : '';
  const realUrl = getImageUrl(imgUrl);
  
  // 默认占位图片
  const defaultImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDMTE2LjU2OSA3MCAxMzAgODMuNDMxIDEzMCAxMDBDMTMwIDExNi41NjkgMTE2LjU2OSAxMzAgMTAwIDEzMEM4My40MzEgMTMwIDcwIDExNi41NjkgNzAgMTAwQzcwIDgzLjQzMSA4My40MzEgNzAgMTAwIDcwWiIgZmlsbD0iI0QxRDVEM0EiLz4KPHBhdGggZD0iTTEwMCAxNDBDMTE2LjU2OSAxNDAgMTMwIDE1My40MzEgMTMwIDE3MEMxMzAgMTg2LjU2OSAxMTYuNTY5IDIwMCAxMDAgMjAwQzgzLjQzMSAyMDAgNzAgMTg2LjU2OSA3MCAxNzBDNzAgMTUzLjQzMSA4My40MzEgMTQwIDEwMCAxNDBaIiBmaWxsPSIjRDFENUQzQSIvPgo8L3N2Zz4K';

  // 调试信息
  console.log('ProductCard Debug:', {
    productName: product.name,
    images: product.images,
    imgUrl,
    API_BASE,
    realUrl,
    finalUrl: realUrl || defaultImage
  });

  return (
    <div 
      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer overflow-hidden"
      onClick={handleCardClick}
    >
      {/* 商品图片 */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={realUrl || defaultImage}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          onError={(e) => {
            console.error('图片加载失败:', realUrl);
            const target = e.target as HTMLImageElement;
            target.src = defaultImage;
          }}
          onLoad={() => {
            console.log('图片加载成功:', realUrl);
          }}
        />
        
        {/* 新品标签 */}
        {product.is_new && (
          <div className="absolute top-2 left-2">
            <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              新品
            </span>
          </div>
        )}
        
        {/* 精选标签 */}
        {product.is_featured && (
          <div className="absolute top-2 right-2">
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              精选
            </span>
          </div>
        )}
      </div>

      {/* 商品信息 */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
          {product.name}
        </h3>
        
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
          {product.description}
        </p>
        
        {/* 评分和评论 */}
        <div className="flex items-center mb-3">
          <div className="flex items-center mr-2">
            {renderStars(product.rating || 0)}
          </div>
          <span className="text-sm text-gray-500">
            ({product.reviews_count || 0} 条评论)
          </span>
        </div>
        
        {/* 价格 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className={`text-2xl font-bold ${product.price === 0 ? 'text-green-500' : 'text-orange-500'}`}>
              {product.price === 0 ? '免费' : `¥${product.price}`}
            </span>
            {product.original_price && product.original_price > product.price && (
              <span className="text-sm text-gray-500 line-through ml-2">
                ¥{product.original_price}
              </span>
            )}
          </div>
          
          {/* 购买按钮 */}
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
              product.price === 0 
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-orange-500 hover:bg-orange-600 text-white'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
          >
            {(() => {
              const userStr = localStorage.getItem('user');
              const isLoggedIn = !!userStr;
              
              if (product.price === 0) {
                return isLoggedIn ? '免费下载' : '免费获取';
              } else {
                return isLoggedIn ? '立即购买' : '查看详情';
              }
            })()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard; 